import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { CounsellingIntakeAssessmentResponse } from './cia.model';
import { Lookups, lookupItems } from '@core/lookup/lookup-utils';
import { ordered } from './cia-lookups';
import { ClinicalBackgroundGroup, FunctionalRiskGroup, TreatmentGroup } from './cia-form-groups';
import { symptomRowComplete } from './cia-validators';
import { nullIfBlank } from '@shared/utils/payload-utils';

// The rows of the assessment that are built from a list (MW-166): one row for every item of it, filled from the saved assessment
// when there is one. And the other way, what these rows send.

export type CollectionName = 'limitations' | 'riskScreenings' | 'symptomClusters';

type FixedRow = Record<string, unknown>;

type FixedRowGroups = {
  clinicalBackground: ClinicalBackgroundGroup;
  functionalRisk: FunctionalRiskGroup;
  treatment: TreatmentGroup;
};

// The lists the fixed rows are built from, in the order of the review form.
function fixedLists(lookups: Lookups) {
  return {
    patterns: ordered(lookups, 'patternTypes'),
    work: ordered(lookups, 'workPerformanceImpactTypes', true),
    factors: ordered(lookups, 'fourPFactorTypes'),
    dimensions: ordered(lookups, 'fourPDimensionTypes'),
    symptoms: ordered(lookups, 'symptomClusterComplexityTypes'),
    stages: lookupItems(lookups, 'developmentStages'),
    limits: ordered(lookups, 'clientLimitationTypes'),
  };
}

// Identifies what the rows were last built from; null while a list they need has not arrived.
export function fixedRowsKey(lookups: Lookups, item: CounsellingIntakeAssessmentResponse | null): string | null {
  const lists = fixedLists(lookups);
  if (Object.values(lists).some((list) => list.length === 0)) {
    return null;
  }

  const { patterns, work, factors, dimensions, symptoms, stages, limits } = lists;
  return `${item?.id ?? 'new'}|${patterns.length}|${work.length}|${factors.length}|${dimensions.length}|${symptoms.length}|${stages.length}|${limits.length}`;
}

// Clears and rebuilds every row that comes from a list, filling each from the saved assessment when there is one.
export function rebuildFixedRows(formBuilder: FormBuilder, lookups: Lookups, item: CounsellingIntakeAssessmentResponse | null, groups: FixedRowGroups): void {
  const { patterns, work, factors, dimensions, symptoms, stages, limits } = fixedLists(lookups);
  const active = <T extends { isActive: boolean }>(rows: T[] | undefined) => (rows ?? []).filter((row) => row.isActive);

  const patternRows = groups.clinicalBackground.controls.patternRatings;
  patternRows.clear();
  for (const type of patterns) {
    const found = active(item?.patternRatings).find((row) => row.patternTypeId === type.id);
    patternRows.push(
      formBuilder.group({
        patternTypeId: [type.id],
        influenceRating: formBuilder.control<number | null>(found?.influenceRating ?? null, Validators.required),
        notes: [found?.notes ?? ''],
      }),
    );
  }

  const workRows = groups.functionalRisk.controls.workPerformanceImpactRatings;
  workRows.clear();
  for (const type of work) {
    const found = active(item?.workPerformanceImpactRatings).find((row) => row.workPerformanceImpactTypeId === type.id);
    workRows.push(
      formBuilder.group({
        workPerformanceImpactTypeId: [type.id],
        impactRating: formBuilder.control<number | null>(found?.impactRating ?? null),
        notes: [found?.notes ?? ''],
      }),
    );
  }

  const factorRows = groups.clinicalBackground.controls.fourPFactors;
  factorRows.clear();
  for (const factor of factors) {
    for (const dimension of dimensions) {
      const found = active(item?.fourPFactors).find((row) => row.fourPFactorTypeId === factor.id && row.fourPDimensionTypeId === dimension.id);
      factorRows.push(formBuilder.group({ fourPFactorTypeId: [factor.id], fourPDimensionTypeId: [dimension.id], notes: [found?.notes ?? ''] }));
    }
  }

  const stageRows = groups.clinicalBackground.controls.developmentStages;
  stageRows.clear();
  for (const stage of stages) {
    const found = active(item?.developmentStages).find((row) => row.developmentStageId === stage.id);
    stageRows.push(formBuilder.group({ developmentStageId: [stage.id], notes: [found?.notes ?? ''] }));
  }

  // The client's limitations: a box for each of the three sides. Rows an earlier assessment held for one side are joined.
  const limitationRows = groups.treatment.controls.limitations;
  limitationRows.clear();
  for (const type of limits) {
    const details = active(item?.limitations)
      .filter((row) => row.clientLimitationTypeId === type.id)
      .map((row) => row.details)
      .filter((text) => !!text)
      .join('\n');
    limitationRows.push(formBuilder.group({ clientLimitationTypeId: [type.id], details: [details] }));
  }

  const symptomRows = groups.treatment.controls.symptomClusters;
  symptomRows.clear();
  symptoms.forEach((type, index) => {
    const found = active(item?.symptomClusters).find((row) => row.symptomClusterComplexityTypeId === type.id);
    symptomRows.push(
      formBuilder.group(
        {
          symptomCluster: [type.name ?? type.code ?? ''],
          symptomClusterComplexityTypeId: [type.id],
          clientPresentation: [found?.clientPresentation ?? ''],
          currentProblemImpactRating: formBuilder.control<number | null>(found?.currentProblemImpactRating ?? null),
          interventionRecommendations: [found?.interventionRecommendations ?? ''],
          interventionImpactRating: formBuilder.control<number | null>(found?.interventionImpactRating ?? null),
          isPrimaryImpact: [false],
          isPrimaryIntervention: [false],
          displayOrder: [index + 1],
        },
        { validators: symptomRowComplete },
      ),
    );
  });
}

// What the fixed rows send: only the rows that have something in them, and the symptom cluster with the biggest ratings
// is the primary impact and the primary intervention.
export function fixedRowPayload(value: {
  functionalRisk: Record<string, unknown>;
  clinicalBackground: Record<string, unknown>;
  treatment: Record<string, unknown>;
}): Record<string, unknown> {
  const text = nullIfBlank;
  const filled = (entry: unknown) => entry !== null && entry !== undefined && entry !== '';
  const functionalRisk = value.functionalRisk;
  const background = value.clinicalBackground;

  const symptoms = (value.treatment['symptomClusters'] as FixedRow[]).filter(
    (row) => text(row['clientPresentation']) || text(row['interventionRecommendations']) || filled(row['currentProblemImpactRating']) || filled(row['interventionImpactRating']),
  );
  const bestImpact = Math.max(0, ...symptoms.map((row) => Number(row['currentProblemImpactRating'] ?? 0)));
  const bestIntervention = Math.max(0, ...symptoms.map((row) => Number(row['interventionImpactRating'] ?? 0)));
  const firstImpact = symptoms.findIndex((row) => Number(row['currentProblemImpactRating'] ?? 0) === bestImpact);
  const firstIntervention = symptoms.findIndex((row) => Number(row['interventionImpactRating'] ?? 0) === bestIntervention);

  return {
    limitations: (value.treatment['limitations'] as FixedRow[])
      .filter((row) => text(row['details']))
      .map((row) => ({ clientLimitationTypeId: row['clientLimitationTypeId'], details: text(row['details']) })),
    patternRatings: (background['patternRatings'] as FixedRow[])
      .filter((row) => filled(row['influenceRating']))
      .map((row) => ({ patternTypeId: row['patternTypeId'], influenceRating: row['influenceRating'], notes: text(row['notes']) })),
    workPerformanceImpactRatings: (functionalRisk['workPerformanceImpactRatings'] as FixedRow[])
      .filter((row) => filled(row['impactRating']))
      .map((row) => ({ workPerformanceImpactTypeId: row['workPerformanceImpactTypeId'], impactRating: row['impactRating'], notes: text(row['notes']) })),
    fourPFactors: (background['fourPFactors'] as FixedRow[])
      .filter((row) => text(row['notes']))
      .map((row) => ({ fourPFactorTypeId: row['fourPFactorTypeId'], fourPDimensionTypeId: row['fourPDimensionTypeId'], notes: text(row['notes']) })),
    developmentStages: (background['developmentStages'] as FixedRow[])
      .filter((row) => text(row['notes']))
      .map((row) => ({ developmentStageId: row['developmentStageId'], notes: text(row['notes']) })),
    symptomClusters: symptoms.map((row, index) => ({
      symptomCluster: row['symptomCluster'],
      symptomClusterComplexityTypeId: row['symptomClusterComplexityTypeId'],
      clientPresentation: text(row['clientPresentation']),
      currentProblemImpactRating: row['currentProblemImpactRating'],
      interventionRecommendations: text(row['interventionRecommendations']),
      interventionImpactRating: row['interventionImpactRating'],
      isPrimaryImpact: index === firstImpact,
      isPrimaryIntervention: index === firstIntervention,
      displayOrder: row['displayOrder'],
    })),
  };
}

// A row of one of the collections that are added to and removed from (rather than built from a list).
export function createCollectionGroup(formBuilder: FormBuilder, name: CollectionName, value?: Record<string, unknown>) {
  const field = (key: string) => value?.[key] ?? '';
  const numericField = (key: string) => value?.[key] ?? null;

  switch (name) {
    case 'limitations':
      return formBuilder.group({
        clientLimitationTypeId: [field('clientLimitationTypeId'), Validators.required],
        details: [field('details')],
      });
    case 'riskScreenings':
      return formBuilder.group({
        riskScreeningTypeId: [field('riskScreeningTypeId'), Validators.required],
        resultSummary: [field('resultSummary')],
        notes: [field('notes')],
      });
    case 'symptomClusters':
      return formBuilder.group({
        symptomCluster: [field('symptomCluster'), Validators.required],
        clientPresentation: [field('clientPresentation')],
        currentProblemImpactRating: [numericField('currentProblemImpactRating'), [Validators.required, Validators.min(1), Validators.max(5)]],
        interventionRecommendations: [field('interventionRecommendations')],
        interventionImpactRating: [numericField('interventionImpactRating'), [Validators.required, Validators.min(1), Validators.max(5)]],
        symptomClusterComplexityTypeId: [field('symptomClusterComplexityTypeId')],
        isPrimaryImpact: [field('isPrimaryImpact') || false],
        isPrimaryIntervention: [field('isPrimaryIntervention') || false],
        displayOrder: [field('displayOrder') || null],
      });
  }
}

// The rows of one collection of the assessment details group.
export function collectionRows(group: FunctionalRiskGroup | TreatmentGroup, name: CollectionName): FormArray {
  return (group.controls as Record<string, unknown>)[name] as FormArray;
}
